import { ModalController } from "@theme/modal-controller";

// Shared across every <localization-form> on the page (header dropdown + the location modal both
// render country-select, so two instances exist). The geo auto-detect must run at most once, and
// opening the modal must reuse the theme's single ModalController rather than spinning up a second.
let detectRan = false;

// Persist the "don't auto-open again" decision in a cookie (not sessionStorage): it has to survive
// normal navigation AND links opened in a new tab, where sessionStorage starts empty and the modal
// would otherwise pop on every fresh tab. 30-day window — long enough to stop nagging, short enough
// that a returning shopper eventually sees the prompt again.
const DISMISS_KEY = "tds_localization_prompt_dismissed";
const DISMISS_MAX_AGE = 30 * 24 * 60 * 60;

function promptDismissed() {
  return document.cookie.split("; ").some((c) => c.startsWith(`${DISMISS_KEY}=`));
}

function dismissPrompt() {
  document.cookie = `${DISMISS_KEY}=1; path=/; max-age=${DISMISS_MAX_AGE}; SameSite=Lax`;
}

// The storefront's default market. A shopper already browsing in GBP/United Kingdom is, by
// definition, where the site wants them, so there is nothing to suggest — skip the whole geo
// lookup rather than open a modal that only ever offers them what they already have.
const DEFAULT_COUNTRY = "GB";

function currentCountryCode() {
  return (
    window.Theme?.localization?.country?.iso_code ||
    window.Shopify?.country ||
    ""
  ).toUpperCase();
}

/**
 * Should the modal be allowed to open ITSELF on page load? Manual opens (the footer globe) are
 * unaffected — this only gates the automatic prompt.
 */
function shouldAutoPrompt() {
  // Never in the theme editor: it steals focus and covers whatever section the merchant is
  // editing, on every reload the customizer does.
  if (window.Shopify?.designMode) return false;
  if (currentCountryCode() === DEFAULT_COUNTRY) return false;
  return !promptDismissed();
}

function getModalController() {
  if (window.__quickfireModalController) return window.__quickfireModalController;
  const instance = new ModalController();
  window.__quickfireModalController = instance;
  return instance;
}

// Closing the location modal (X or overlay — both dispatch this event) counts as a dismissal.
// Registered at module scope so it binds exactly once regardless of how many forms are on the page.
window.addEventListener("location-selector-id-modal-closed", dismissPrompt);

/**
 * The country rows, fetched once and shared by both pickers.
 *
 * country-select renders twice per page — header and location-selector modal — and the two lists
 * were byte-identical: 165,752 bytes each, 331,504 in total, 28.1% of the homepage, plus 388
 * `image_url` calls per render paid inside TTFB. They now come from sections/country-list.liquid
 * on demand, and one fetch serves both instances.
 */
let countryRowsPromise = null;

function fetchCountryRows(url) {
  if (countryRowsPromise) return countryRowsPromise;

  countryRowsPromise = fetch(url, { credentials: "same-origin" })
    .then((response) => {
      if (!response.ok) throw new Error(`Country list failed: ${response.status}`);
      return response.text();
    })
    .then((html) => {
      // Parsed inside a <template>, not with DOMParser: a bare <li> outside list context can be
      // relocated or dropped by the HTML parser, and the Section Rendering API wraps the response
      // in its own <div>. Template content parses permissively and keeps the rows intact.
      const holder = document.createElement("template");
      holder.innerHTML = html;
      const rows = holder.content.querySelectorAll("li");
      if (!rows.length) throw new Error("No country rows in section response");
      return rows;
    })
    .catch((error) => {
      // Drop the cache so the next open retries instead of replaying one failure forever.
      countryRowsPromise = null;
      throw error;
    });

  return countryRowsPromise;
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button"),
    };
    this.elements.button.addEventListener("click", this.openSelector.bind(this));
    this.elements.button.addEventListener("focusout", this.closeSelector.bind(this));
    this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
    // ONE delegated listener instead of one per row. country-select renders 194 countries and sits
    // on the page twice, so the previous per-<a> binding cost 388 listeners on every page load -
    // and it cannot work at all now the rows are fetched on first open, because the anchors do not
    // exist in the DOM at construction time.
    this.addEventListener("click", this.onItemClick.bind(this));

    // Warm the list on intent so the rows are usually in place before the click lands. Cheap:
    // hydratePanel() is idempotent and one fetch is shared across both pickers.
    const prefetch = this.hydratePanel.bind(this);
    this.elements.button.addEventListener("pointerenter", prefetch, { once: true, passive: true });
    this.elements.button.addEventListener("focus", prefetch, { once: true });

    if (shouldAutoPrompt()) {
      this.detectLocation();
    }
  }
  /**
   * The panel is resolved on demand, never captured in the constructor: country-select ships an
   * empty <ul> and the 194 rows are fetched into it on first open. Capturing it early was also the
   * latent bug that made hidePanel() throw if the element were ever absent.
   */
  get panel() {
    return this.querySelector("ul");
  }

  /**
   * Fill the <ul> with country rows the first time it is needed.
   *
   * Returns the panel synchronously so openSelector() can reveal it immediately — the rows land
   * when the fetch resolves, which with the prefetch in the constructor is usually before the
   * click. Idempotent: `data-country-list-state` stops a second open refetching. The two language
   * pickers keep their options inline and carry no data-country-list-url, so they fall straight
   * through, exactly as they did when this method cloned a <template>.
   * @returns {HTMLElement|null} the panel, or null if this instance has none
   */
  hydratePanel() {
    const panel = this.panel;
    if (!panel) return null;

    const url = panel.dataset.countryListUrl;
    if (!url || panel.dataset.countryListState) return panel;

    panel.dataset.countryListState = "loading";
    panel.setAttribute("aria-busy", "true");
    fetchCountryRows(url)
      .then((rows) => {
        // Clone: one parse is shared between both pickers, and appending a node would move it out
        // of the other one.
        panel.append(...Array.from(rows, (row) => row.cloneNode(true)));
        panel.dataset.countryListState = "ready";
      })
      .catch((error) => {
        // Leave no state behind, so the next open retries.
        delete panel.dataset.countryListState;
        console.warn("localization-form: could not load country rows", error);
      })
      .finally(() => panel.removeAttribute("aria-busy"));

    return panel;
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.panel?.setAttribute("hidden", true);
  }
  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;
    this.hidePanel();
    this.elements.button.focus();
  }
  onItemClick(event) {
    // Delegated, so resolve the row from the event target rather than currentTarget (which is now
    // the <localization-form> host). Anything that is not a country/language row - the toggle
    // button, the panel chrome - falls through untouched.
    const item = event.target.closest("a[data-value]");
    if (!item || !this.contains(item)) return;
    event.preventDefault();
    const form = this.querySelector("form");
    this.elements.input.value = item.dataset.value;
    if (form) {
      dismissPrompt();
      form.submit();
    }
  }
  openSelector() {
    this.elements.button.focus();
    const panel = this.hydratePanel();
    if (!panel) return;
    panel.toggleAttribute("hidden");
    this.elements.button.setAttribute(
      "aria-expanded",
      (this.elements.button.getAttribute("aria-expanded") === "false").toString(),
    );
  }
  closeSelector(event) {
    const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === "BUTTON";
    if (event.relatedTarget === null || shouldClose) {
      this.hidePanel();
    }
  }
  async detectLocation() {
    // Only the first instance to reach here does the geo lookup / auto-open; the other bails out.
    if (detectRan) return;
    detectRan = true;
    try {
      const response = await fetch(
        window.Shopify.routes.root +
          "browsing_context_suggestions.json" +
          "?country[enabled]=true" +
          `&country[exclude]=${window.Shopify.country}` +
          "&language[enabled]=true" +
          `&language[exclude]=${window.Shopify.language}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.suggestions && data.suggestions.length > 0) {
        const suggestion = data.suggestions[0];
        const currentCountry =
          window.Theme?.localization?.country?.iso_code || window.Shopify.country;

        if (
          suggestion.parts.country?.handle === currentCountry ||
          suggestion.parts.country?.name === window.Theme?.localization?.country?.name
        ) {
          return;
        }

        const modal = document.querySelector('[data-modal="location-selector-id"]');

        if (!modal) {
          console.warn("Location selector modal not found in DOM");
          return;
        }

        // Mark dismissed as soon as we auto-show it: the shopper has now seen the prompt once, so it
        // shouldn't reappear on later pages whether they close it or just navigate away.
        dismissPrompt();
        getModalController().presentModal("location-selector-id");
      }
    } catch (error) {
      console.error("Error fetching browsing context suggestions:", error);
    }
  }
}
customElements.define("localization-form", LocalizationForm);
